<template>
    <loading-page-layout class="activity-page" :loaded="loaded">
        <expired-offers-snackbar :is-offers-expired="isOffersExpired" @refresh="goToSearchPage" />
        <teleport v-if="$breakpoint.smAndDown" to="#under-header">
            <mobile-info-page-tabs v-if="$breakpoint.smAndDown" v-model="mobileTabs" />
        </teleport>
        <v-container class="mt-16 mt-md-0" fluid>
            <activity-breadcrumbs v-if="!$breakpoint.smAndDown" :search-request="searchRequest" :product="product" />
            <v-row>
                <v-col v-show="!$breakpoint.smAndDown || ($breakpoint.smAndDown && mobileTabs === 0)" md="8">
                    <h1 v-if="product.info" class="activity-page__title text-h6">
                        {{ product.info.name }}
                        <v-chip v-if="product.type && product.type !== 'UNKNOWN'" label size="x-small">
                            {{ $t(`filters_activityType.${product.type}`) }}
                        </v-chip>
                    </h1>
                    <supplier-name-label v-if="isTO1" :supplier-name="product.supplierName" class="mb-2" />
                    <component
                        :is="!$breakpoint.smAndDown ? 'product-gallery' : 'product-gallery-mobile'"
                        :images-data="uniqueImages"
                    />
                    <template v-if="!$breakpoint.smAndDown">
                        <activity-grouped-offers
                            :sorted-filtered-offers="sortedFilteredOffers"
                            :dates-for-filter="datesForFilter"
                            full-card
                            class="mt-4"
                            @images="images = [...images, ...$event]"
                            @info="offersInfo = [...offersInfo, $event]"
                            @on-date-filter-change="onDateFilterChange"
                        />
                        <template v-if="groupOffersBy === 'none'">
                            <activity-offer-card
                                v-for="offer in sortedFilteredOffers"
                                :key="offer.offerKey"
                                :offer="offer"
                                @images="images = [...images, ...$event]"
                                @info="offersInfo = [...offersInfo, $event]"
                            />
                        </template>
                    </template>
                    <activity-duration
                        v-if="offersInfo[0]"
                        :time="offersInfo[0].durationTime"
                        :days="offersInfo[0].durationDays"
                        class="mt-4"
                    />
                    <template v-if="showShortDescription">
                        <div class="font-weight-bold mt-2">
                            {{ $t(`description_type.short`) }}
                        </div>
                        <div v-html="shortDescription" />
                    </template>
                    <template v-if="fullDescription">
                        <div class="font-weight-bold mt-2">
                            {{ $t(`description_type.general`) }}
                        </div>
                        <div v-html="fullDescription" />
                    </template>
                    <div v-if="descriptions && descriptions.length" class="activity-page__descr d-flex flex-column">
                        <strong class="activity-page__descr-title">{{ $t('description') }} </strong>
                        <div
                            v-for="(description, index) in descriptions"
                            :key="index"
                            class="hotel-info-descriptions__item"
                        >
                            <h4 class="hotel-info-descriptions__item-title">{{ descriptionTypeLabel(description) }}</h4>
                            <div class="activity-page__descr-item mb-4" v-html="description.description" />
                        </div>
                    </div>
                    <view-offers-button @show-offers="mobileTabs = 1" />
                </v-col>
                <v-col v-show="!$breakpoint.smAndDown || ($breakpoint.smAndDown && mobileTabs === 1)" md="4">
                    <activity-search-summary :search-request="searchRequest" show-dates />
                    <template v-if="$breakpoint.smAndDown">
                        <activity-grouped-offers
                            :sorted-filtered-offers="sortedFilteredOffers"
                            :dates-for-filter="datesForFilter"
                            full-card
                            @images="images = [...images, ...$event]"
                            @info="offersInfo = [...offersInfo, $event]"
                            @on-date-filter-change="onDateFilterChange"
                        />
                        <template v-if="groupOffersBy === 'none'">
                            <activity-offer-card
                                v-for="(offer, offerIndex) in sortedFilteredOffers"
                                :key="offer.offerKey"
                                :offer="offer"
                                :data-cy="`activity-offer-card-${offerIndex}`"
                                @images="images = [...images, ...$event]"
                                @info="offersInfo = [...offersInfo, $event]"
                            />
                        </template>
                    </template>
                    <div v-if="!$breakpoint.smAndDown">
                        <client-only>
                            <activity-map :offers="mapPoints" :city="city" style="position: relative; top: 0" />
                        </client-only>
                    </div>
                </v-col>
            </v-row>
        </v-container>
        <conditions-modal>
            <template v-slot:info>
                <activity-conditions-modal-info />
            </template>
        </conditions-modal>
        <activity-info-modal />
    </loading-page-layout>
</template>

<script lang="ts">
    import {Component, mixins} from 'vue-facing-decorator'
    import MobileInfoPageTabs from '@/components/snippets/MobileInfoPageTabs.vue'
    import ConditionsModal from '~/components/modals/ConditionsModal.vue'
    import ActivityBreadcrumbs from '@/components/activities/ActivityBreadcrumbs.vue'
    import ActivityOfferCard from '@/components/activities/info/ActivityOfferCard.vue'
    import ActivityInfo from '@/components/activities/info/ActivityInfo.vue'
    import ActivitySearchSummary from '@/components/activities/search/ActivitySearchSummary.vue'
    import ActivityMap from '@/components/activities/search/ActivityMap.vue'
    import ActivityInfoModal from '@/components/activities/info/ActivityInfoModal.vue'
    import {ActivityStoreMixin} from '@/mixins/ActivityStoreMixin.vue'
    import ActivityGroupedOffers from '~src/components/activities/search/offers/ActivityGroupedOffers.vue'
    import ViewOffersButton from '@/components/snippets/ViewOffersButton.vue'
    import ProductGallery from '@/components/info/ProductGallery.vue'
    import ProductGalleryMobile from '@/components/info/ProductGalleryMobile.vue'
    import {ActivityPageMixin} from '@/components/activities/mixins/ActivityPageMixin.vue'
    import ExpiredOffersSnackbar from '@/components/search/ExpiredOffersSnackbar.vue'
    import ActivityConditionsModalInfo from '@/components/activities/snippets/ActivityConditionsModalInfo.vue'
    import {handleNewLine} from '@/utils/filters'
    import LoadingPageLayout from '@/components/LoadingPageLayout.vue'
    import {SearchPageMixin} from '@/components/mixins/SearchPageMixin.vue'
    import {InfoPageMixin} from '@/components/mixins/InfoPageMixin.vue'
    import ActivityDuration from '@/components/activities/search/offers/ActivityDuration.vue'
    import {SearchPageAuthMixin} from '@/components/mixins/SearchPageAuthMixin.vue'
    import SupplierNameLabel from '@/components/hotels/snippets/SupplierNameLabel.vue'
    import {sortOffersInsideProduct} from '@/filters/activitiesSort'
    import {useActivityRuntimeData} from '~/composables/activity'

    @Component({
        components: {
            ActivityDuration,
            LoadingPageLayout,
            ActivityConditionsModalInfo,
            ExpiredOffersSnackbar,
            ViewOffersButton,
            ActivityGroupedOffers,
            ActivityOfferCard,
            ActivityBreadcrumbs,
            MobileInfoPageTabs,
            ActivityInfo,
            ActivitySearchSummary,
            ActivityMap,
            ConditionsModal,
            ActivityInfoModal,
            ProductGallery,
            ProductGalleryMobile,
            SupplierNameLabel,
        },
        async setup() {
            const route = useRoute()
            await useAsyncData(`activity-${route.query.countryId}-${route.query.cityId}`, async () => {
                await useActivityRuntimeData(route)
                return true
            })
        },
    })
    export class ActivityPage extends mixins(
        ActivityPageMixin,
        ActivityStoreMixin,
        SearchPageMixin,
        InfoPageMixin,
        SearchPageAuthMixin
    ) {
        offersInfo = []
        dateFilter = null

        onDateFilterChange(date) {
            this.dateFilter = date
        }

        descriptionTypeLabel(description) {
            if (this.$te(`description_type.${description?.type.toLowerCase()}`)) {
                return this.$t(`description_type.${description?.type.toLowerCase()}`)
            }

            return description?.type
        }

        get datesForFilter() {
            return this.product.offers?.map(o => o.date) || []
        }

        get sortedFilteredOffers() {
            let offers = [...(this.product.offers || [])]
            sortOffersInsideProduct[this.sortKey]?.({offers})
            if (this.dateFilter) {
                offers = offers.filter(offer => offer.date === this.dateFilter)
            }
            return offers
        }

        get sortKey() {
            return activitiesStore.sortFnName
        }

        get fullDescription() {
            return handleNewLine(this.offersInfo.find(({fullDescription}) => fullDescription)?.fullDescription)
        }

        get shortDescription() {
            return handleNewLine(this.offersInfo.find(({shortDescription}) => shortDescription)?.shortDescription)
        }

        get descriptions() {
            return this.offersInfo.find(({descriptions}) => descriptions?.length)?.descriptions
        }

        get showShortDescription() {
            return (
                this.shortDescription &&
                (!this.fullDescription || this.fullDescription.indexOf(this.shortDescription) === -1)
            )
        }

        get uniqueImages() {
            return this.images.filter((v, i, a) => a.findIndex(t => t.url === v.url) === i)
        }

        get city() {
            return this.productRuntimeStore.city
        }

        get groupOffersBy() {
            return this.productStore.groupOffersBy
        }

        get mapPoints() {
            return this.product.offers?.reduce((departures, offer) => {
                if (offer.pickUpPoints && offer.pickUpPoints.length && this.offersInfo.length) {
                    offer.pickUpPoints.forEach(point => {
                        if (
                            point.latitude &&
                            point.longitude &&
                            !departures.some(
                                item => item.latitude === point.latitude && item.longitude === point.longitude
                            )
                        ) {
                            const data = {
                                pointName: point.name,
                                startTimes: offer.startTimes,
                                latitude: point.latitude,
                                longitude: point.longitude,
                                name: this.offersInfo[0].name,
                            }
                            departures.push(data)
                        }
                    })
                }

                return departures
            }, [])
        }

        get isTO1() {
            return authStore.isTO1
        }
    }

    export default ActivityPage
</script>

<style scoped lang="scss">
    .disable-top {
        top: 0 !important;
    }
</style>
